import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { CategoryView } from "@/lib/api/types";
import { pickLocalized } from "@/lib/i18n/localized";
import {
  CategoryTile,
  ShareBar,
  CategoryActionsMenu,
} from "@/modules/categories/components/category-ui";
import {
  catalogShare,
  categoryColorIndex,
  categoryTile,
} from "@/modules/categories/lib/category-display";

type CategoriesTableViewProps = {
  locale: Locale;
  categories: CategoryView[];
  counts: Map<string, number>;
  totalProducts: number;
  onEdit: (category: CategoryView) => void;
  onDelete: (category: CategoryView) => void;
};

const GRID_COLUMNS =
  "[grid-template-columns:30px_2.4fr_.8fr_1.7fr_.7fr]";

function GripIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
      <circle cx="9" cy="6" r="1.4" />
      <circle cx="15" cy="6" r="1.4" />
      <circle cx="9" cy="12" r="1.4" />
      <circle cx="15" cy="12" r="1.4" />
      <circle cx="9" cy="18" r="1.4" />
      <circle cx="15" cy="18" r="1.4" />
    </svg>
  );
}

export function CategoriesTableView({
  locale,
  categories,
  counts,
  totalProducts,
  onEdit,
  onDelete,
}: CategoriesTableViewProps) {
  const t = useTranslations();

  return (
    <div className="overflow-x-auto rounded-[16px] border border-[color:var(--line2)] bg-[color:var(--card)]">
      <div className="min-w-[820px]">
        <div
          className={`grid ${GRID_COLUMNS} items-center gap-2 border-b border-[color:var(--line)] bg-[color:var(--soft)] px-[18px] py-[13px] text-[12px] font-semibold text-[color:var(--text-muted)]`}
        >
          <span />
          <span>{t(keys.categories.tableCategory)}</span>
          <span>{t(keys.categories.tableProducts)}</span>
          <span>{t(keys.categories.tableShare)}</span>
          <span className="text-end">{t(keys.categories.tableEdit)}</span>
        </div>

        {categories.map((category) => {
          const name =
            pickLocalized(locale, category.nameAr, category.nameEn) ??
            category.nameEn ??
            category.nameAr;
          const tile = categoryTile(categoryColorIndex(category.id));
          const productCount = counts.get(category.id) ?? 0;
          const share = catalogShare(productCount, totalProducts);
          return (
            <div
              key={category.id}
              className={`grid ${GRID_COLUMNS} items-center gap-2 border-b border-[color:var(--hair)] px-[18px] py-[10px] text-[13px] transition-colors last:border-b-0 hover:bg-[color:var(--soft)]`}
            >
              <span className="flex items-center justify-center text-[color:var(--text-disabled)]">
                <GripIcon />
              </span>
              <span className="flex min-w-0 items-center gap-[11px] font-semibold">
                <CategoryTile bg={tile.bg} fg={tile.fg} size={32} radius={9} iconSize={16} />
                <span className="truncate text-[color:var(--text-primary)]">{name}</span>
                {/* Badge on the backend ACTIVE-product count (matches buyer
                    visibility), not the client-side catalog `productCount`
                    above — see CategoryCard for the full rationale. */}
                {category.productCount === 0 ? (
                  <span className="shrink-0 whitespace-nowrap rounded-full border border-[color:var(--warning-500)]/30 bg-[color:var(--warning-100)] px-2 py-[3px] text-[11px] font-semibold text-[color:var(--warning-700)]">
                    {t(keys.categories.emptyBadge)}
                  </span>
                ) : null}
              </span>
              <span className="font-semibold text-[color:var(--text-secondary)]">
                {productCount}
              </span>
              <span className="flex items-center gap-2.5">
                <ShareBar pct={share} color={tile.fg} className="max-w-[110px] flex-1" />
                <span className="text-[12.5px] font-bold text-[color:var(--text-primary)]">
                  {share}%
                </span>
              </span>
              <span className="flex items-center justify-end">
                <CategoryActionsMenu
                  onEdit={() => onEdit(category)}
                  onDelete={() => onDelete(category)}
                />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
